/**
 * Testo legale delle offerte: un solo punto da cui leggono PDF, anteprima ed Excel,
 * così il documento non può dire cose diverse a seconda del formato.
 *
 * I nomi «Fornitore», «Cliente» e «Offerta» sono quelli del contratto allegato
 * (public/Contratto Abruzzo AI VENDITA.pdf), di cui l'Offerta è parte integrante (art. 1.1).
 *
 * Nel testo vanno solo caratteri che Helvetica di jsPDF sa stampare: niente «€», «→», «✓».
 *
 * Tre frasi della Clausola B sembrano sostituibili e non lo sono:
 * - «esclusivo interesse del Fornitore»: una condizione si presume bilaterale se non è scritto
 *   il contrario (Cass. 5692/2012 e 10220/1996); senza, il cliente che ci ripensa potrebbe
 *   usare la mancanza dei requisiti per uscire dall'accordo.
 * - «oggettivamente necessari», mai «a insindacabile giudizio»: una condizione sospensiva che
 *   dipende dalla sola volontà di chi si obbliga è nulla (art. 1355 c.c.).
 * - «Salvi i casi di dolo o colpa grave»: senza, la limitazione di responsabilità è nulla
 *   (art. 1229 c.c.).
 *
 * Se si aggiunge una clausola onerosa va citata anche in OFFER_SPECIFIC_APPROVAL_TEXT: la
 * firma separata vale solo per le clausole richiamate una per una (art. 1341 c.c.).
 */

export const OFFER_TERMS_TITLE = 'CONDIZIONI CONTRATTUALI';

export interface OfferClause {
  id: 'A' | 'B';
  title: string;
  text: string;
  items?: readonly string[];
}

export const OFFER_REQUIREMENTS_TITLE = 'Elenco dei requisiti minimi (Clausola B)';

export const OFFER_CLAUSES: readonly OfferClause[] = [
  {
    id: 'A',
    title: "Clausola A — Validità dell'Offerta",
    text:
      "La presente Offerta è valida per 1 (un) mese dalla data della sua consegna al Cliente, intesa come data di ricezione con qualunque mezzo (e-mail, PEC, consegna a mani) o, se tale data non è dimostrabile, dalla data riportata nell'Offerta. " +
      "L'accettazione deve avvenire per iscritto, mediante sottoscrizione dell'Offerta, e pervenire al Fornitore entro tale termine. " +
      "Decorso inutilmente il termine, l'Offerta perde ogni efficacia e si intende annullata di diritto, senza necessità di alcun avviso, e prezzi, tempi e condizioni in essa indicati non vincolano più il Fornitore. " +
      "L'accettazione tardiva produce effetti solo se il Fornitore ne dà immediato avviso scritto al Cliente (art. 1326 c.c.). " +
      "Il termine indicato è un termine di efficacia e non di irrevocabilità: l'Offerta non costituisce proposta irrevocabile ai sensi dell'art. 1329 c.c. e il Fornitore può revocarla finché il contratto non è concluso (art. 1328 c.c.).",
  },
  {
    id: 'B',
    title: 'Clausola B — Requisiti minimi del Cliente',
    text:
      "L'Offerta è valida ed eseguibile a condizione che il Cliente disponga, a propria cura e spese, dei requisiti minimi hardware e infrastrutturali aziendali elencati di seguito, salvo diversa indicazione scritta del Fornitore, e di quanto altro risulti oggettivamente necessario all'installazione e al corretto funzionamento di quanto offerto. " +
      "Con l'accettazione il Cliente dichiara di possederli e si impegna a consentirne la verifica. " +
      "Se i requisiti risultano mancanti o inadeguati, il Fornitore ne dà comunicazione scritta motivata al Cliente e può, senza che ciò costituisca inadempimento: (a) sospendere le attività, con proroga dei relativi termini, fino all'adeguamento a carico del Cliente; (b) proporre un'integrazione dell'Offerta con i relativi costi; (c) se l'adeguamento non avviene entro 30 (trenta) giorni dalla comunicazione, recedere dal contratto per iscritto, con diritto al pagamento delle attività già svolte e dei costi già sostenuti. " +
      "La presente condizione è posta nell'esclusivo interesse del Fornitore, che soltanto può avvalersene o rinunciarvi: il Cliente non può invocarla per sottrarsi ai propri obblighi. " +
      "Salvi i casi di dolo o colpa grave, il Fornitore non risponde di ritardi, malfunzionamenti o mancati risultati dipendenti dalla mancanza o inadeguatezza dei requisiti.",
    items: [
      'Connessione: Internet in fibra (FTTC o FTTH) stabile e continuativa, con velocità minima di 100 Mbps in download e 20 Mbps in upload.',
      'Rete aziendale: cablata o Wi-Fi con copertura stabile nelle aree di lavoro; firewall e sistemi di sicurezza configurati per non bloccare i servizi offerti.',
      'Computer: processore Intel Core i5 di ottava generazione, AMD Ryzen 5, Apple M1 o superiori, almeno 8 GB di RAM, disco SSD con almeno 20 GB liberi, schermo Full HD.',
      'Software: sistema operativo e browser (Chrome, Edge, Safari o Firefox) aggiornati e supportati dal produttore, con protezione antivirus attiva.',
      'Dispositivi mobili, ove previsti: smartphone o tablet con iOS o Android aggiornati e supportati dal produttore.',
      'Account e accessi: e-mail aziendale attiva per ogni utente e accessi con diritti di amministratore a domini, caselle e-mail e servizi di terzi da collegare (es. WhatsApp Business, Meta, Google), con relative licenze e utenze a carico del Cliente.',
    ],
  },
];

export const OFFER_SPECIFIC_APPROVAL_TITLE = 'Approvazione specifica ai sensi degli artt. 1341 e 1342 c.c.';

export const OFFER_SPECIFIC_APPROVAL_TEXT =
  "Il Cliente dichiara di aver letto e di approvare specificamente: Clausola A (Validità dell'Offerta: annullamento alla scadenza, inefficacia dell'accettazione tardiva, revocabilità dell'Offerta); " +
  'Clausola B (Requisiti minimi del Cliente: requisiti a carico del Cliente, sospensione delle attività, recesso del Fornitore, condizione nell\'esclusivo interesse del Fornitore, limitazione di responsabilità).';

/** Le condizioni escono sempre, tranne quando è stata tolta la spunta «Stampa condizioni». */
export function shouldPrintOfferTerms(printConditions: boolean | null | undefined): boolean {
  return printConditions !== false;
}

/** Numera un elenco nello stesso modo in PDF ed Excel. */
export function numberOfferItems(items: readonly string[] = []): string[] {
  return items.map((item, index) => `${index + 1}. ${item}`);
}

/** Spezza un testo lungo in righe: nelle celle Excel esportate non si può andare a capo. */
export function wrapOfferText(text: string, maxChars: number): string[] {
  const lines: string[] = [];
  let current = '';
  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && candidate.length > maxChars) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}
